import { useState, useRef, useCallback, createContext, useContext } from 'react';

// ── Toast Hook ────────────────────────────────────────────────────────────────

export interface ToastContextValue {
  /** 目前顯示的 toast 訊息，null 代表無 */
  toast: string | null;
  /** 顯示 toast 訊息，3 秒後自動清除 */
  showToast: (message: string) => void;
  /** 手動清除 toast */
  clearToast: () => void;
}

/** 建立 toast 狀態與自動清除計時器的邏輯 */
export function useToastState(): ToastContextValue {
  const [toast, setToast] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((message: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast(message);
    timerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const clearToast = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast(null);
  }, []);

  return { toast, showToast, clearToast };
}

// ── Toast Context（供跨元件使用） ─────────────────────────────────────────────

export const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = (): ToastContextValue => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
